import { visit } from '../libs/Visitor'
import { IAM } from '../types/iam'
import { DataResponse } from '../types/response'

export type CreatePolicyResponse = DataResponse<IAM.PolicyAggregate>
export type FetchPoliciesResponse = DataResponse<{
  [k: string]: Array<IAM.PolicyAggregate>
}>

export const iam = {
  fetchSlackUser(email: string) {
    return visit<FetchPoliciesResponse>({
      method: 'GET',
      url: 'iam/slack-user',
      params: {
        email
      }
    })
  },

  category: {
    fetchAll() {
      return visit<DataResponse<IAM.Category[]>>({
        method: 'GET',
        url: 'iam/categories'
      })
    }
  },

  user: {
    fetch(userId: number) {
      return visit<DataResponse<IAM.UserAggregate>>({
        method: 'GET',
        url: 'iam/users/' + userId
      })
    },
    fetchByEmail(email: string) {
      return visit<DataResponse<IAM.UserAggregate[]>>({
        method: 'GET',
        url: 'iam/users?email=' + email,
      })
    },
    fetchAll() {
      return visit<DataResponse<IAM.UserAggregate[]>>({
        method: 'GET',
        url: 'iam/users'
      })
    },
    create(
      user: IAM.UserAggregate,
      policyNames: Array<string> = [],
      groupIds: Array<number> = [],
      roleIds: Array<number> = []
    ) {
      delete user.groups
      delete user.roles
      delete user.policies
      return visit<DataResponse<IAM.UserAggregate>>({
        method: 'POST',
        url: 'iam/users',
        data: {
          ...user,
          policyNames,
          roleIds,
          groupIds
        }
      })
    },
    edit(user: IAM.User) {
      return visit<DataResponse<number>>({
        method: 'PUT',
        url: 'iam/users/' + user.id,
        data: user
      })
    },
    delete(userIds: number[]) {
      return visit<DataResponse<boolean>>({
        method: 'DELETE',
        url: 'iam/users',
        data: { userIds }
      })
    },

    updateV2(user: IAM.UserAggregate) {
      const policies = user?.policies?.map(p => p.name) 
      const groups = user?.groups?.map(g => g.id) 
      const roles = user?.roles?.map(r => r.id) 
      return visit<DataResponse<Boolean>>({
        method: 'PUT',
        url: 'iam/users/' + user.id,
        data: { ...user, policies, roles, groups }
      })
    },

    update(user: IAM.UserAggregate, policyNames: Array<string>, groupIds: Array<number>, roleIds: Array<number>) {
      delete user.groups
      delete user.roles
      delete user.policies
      delete user.updatedAt
      delete user.createdAt
      return visit<DataResponse<Boolean>>({
        method: 'PUT',
        url: 'iam/users/' + user.id,
        data: {
          ...user,
          policies: policyNames,
          roles: roleIds,
          groups: groupIds
        }
      })
    }
  },

  group: {
    fetch(groupId: number) {
      return visit<DataResponse<IAM.GroupAggregate>>({
        method: 'GET',
        url: 'iam/groups/' + groupId
      })
    },
    fetchAll() {
      return visit<DataResponse<IAM.GroupAggregate[]>>({
        method: 'GET',
        url: 'iam/groups'
      })
    },
    create(group: IAM.Group) {
      return visit<DataResponse<IAM.GroupAggregate>>({
        method: 'POST',
        url: 'iam/groups',
        data: group
      })
    },
    delete(groupIds: number[]) {
      return visit<DataResponse<boolean>>({
        method: 'DELETE',
        url: 'iam/groups',
        data: { groupIds }
      })
    },
    update(group: IAM.GroupAggregate, userIds: Array<number>, policyNames: Array<string>) {
      delete group.policies
      delete group.users
      return visit<DataResponse<Boolean>>({
        method: 'PUT',
        url: 'iam/groups/' + group.id,
        data: {
          ...group,
          users: userIds,
          policies: policyNames
        }
      })
    }
  },

  role: {
    fetch(roleId: number) {
      return visit<DataResponse<IAM.RoleAggregate>>({
        method: 'GET',
        url: 'iam/roles/' + roleId
      })
    },

    create(role: IAM.Role) {
      return visit<DataResponse<any>>({
        method: 'POST',
        url: 'iam/roles',
        data: role
      })
    },

    delete(roleIds: number[]) {
      return visit<DataResponse<boolean>>({
        method: 'DELETE',
        url: 'iam/roles',
        data: { roleIds }
      })
    },

    fetchAll() {
      return visit<DataResponse<IAM.RoleAggregate[]>>({
        method: 'GET',
        url: 'iam/roles'
      })
    },

    update(role: IAM.RoleAggregate, userIds: Array<number>, policyNames: Array<string>) {
      delete role.policies
      delete role.users
      return visit<DataResponse<Boolean>>({
        method: 'PUT',
        url: 'iam/roles/' + role.id,
        data: {
          ...role,
          users: userIds,
          policies: policyNames
        }
      })
    }
  },

  policy: {
    fetch(policyName: string) {
      return visit<DataResponse<IAM.PolicyAggregate>>({
        method: 'GET',
        url: 'iam/policies/' + policyName
      })
    },
    fetchAll() {
      return visit<DataResponse<IAM.PolicyAggregate[]>>({
        method: 'GET',
        url: 'iam/policies',
        headers: {
          'Cache-Control': 'no-store'
        }
      })
    },
    create(data: IAM.Policy) {
      return visit<CreatePolicyResponse>({
        method: 'POST',
        url: 'iam/policies',
        data
      })
    },
    delete(policyNames: string[]) {
      return visit<DataResponse<boolean>>({
        method: 'DELETE',
        url: 'iam/policies',
        data: { policyNames }
      })
    },
    update(policy: IAM.PolicyAggregate, userIds: Array<number>, groupIds: Array<number>, roleIds: Array<number>) {
      return visit<DataResponse<Boolean>>({
        method: 'PUT',
        url: 'iam/policies/' + policy.name,
        data: {
          ...policy,
          users: userIds,
          roles: roleIds,
          groups: groupIds
        }
      })
    }
  },

  dashboard: {
    fetch() {
      return visit<DataResponse<IAM.DashboardData>>({
        method: 'GET',
        url: 'iam/dashboard-overview'
      })
    },
  }
}
